import {
  ADRESSVORSCHLAG_FAILED_REASON,
  VermietetesBasisModalObjekt,
  VermietetesBebautesModalObjektChangedPayload,
  VermietetesModalObjekt,
  VermietetesModalObjektChangedPayload,
  VermietetesObjektEnum,
  VermietetesUnbebautesModalObjektChangedPayload,
} from '@ps20-frontend/frontend-models';
import { AnyAction } from 'redux';
import {
  AdressvorschlagFailedPayload,
  AdressvorschlagPayload,
  CALLER_ID,
  GlobalActions,
} from '@ps20-frontend/shop-integration-redux';
import { RsActions } from '../rsActions';

import { IAppState } from '../appState';

export const KEINE_AUSWAHL = 'Keine Auswahl';

function applyBebautesObjektChanges(
  vermietetesBebautesObjekt: VermietetesModalObjekt,
  changes: VermietetesBebautesModalObjektChangedPayload['changes'],
): VermietetesModalObjekt {
  if (vermietetesBebautesObjekt.grundstueckBebaut === VermietetesObjektEnum.BEBAUT) {
    if (changes.wohneinheiten === 0) {
      return {
        ...vermietetesBebautesObjekt,
        wohneinheiten: 0,
        bruttojahresmieteWohneinheiten: null,
      };
    }

    if (changes.gewerbeeinheiten === 0) {
      return {
        ...vermietetesBebautesObjekt,
        gewerbeeinheiten: 0,
        bruttojahresmieteGewerbeeinheiten: null,
      };
    }

    if (changes.stellplaetze === 0) {
      return {
        ...vermietetesBebautesObjekt,
        stellplaetze: 0,
        bruttojahresmieteStellplaetze: null,
      };
    }

    return {
      ...vermietetesBebautesObjekt,
      ...changes,
    };
  }

  if (changes.grundstueckBebaut === VermietetesObjektEnum.BEBAUT) {
    return {
      ...vermietetesBebautesObjekt,
      strasse:
        vermietetesBebautesObjekt.strasse === KEINE_AUSWAHL
          ? null
          : vermietetesBebautesObjekt.strasse,
      grundstueckBebaut: VermietetesObjektEnum.BEBAUT,
      wohneinheiten: 0,
      gewerbeeinheiten: 0,
      stellplaetze: 0,
      bruttojahresmieteWohneinheiten: null,
      bruttojahresmieteGewerbeeinheiten: null,
      bruttojahresmieteStellplaetze: null,
    };
  }

  return vermietetesBebautesObjekt;
}

export function vermieterModalReducer(
  sectionState: Array<VermietetesModalObjekt> = null,
  action: AnyAction,
  appState: IAppState,
): Array<VermietetesModalObjekt> {
  switch (action.type) {
    case RsActions.VERMIETETES_MODAL_OBJEKT_CHANGED: {
      const actionPayload: VermietetesModalObjektChangedPayload = action.payload;
      return sectionState.map((vermietetesObjekt: VermietetesModalObjekt, index: number): VermietetesModalObjekt => {
        if (actionPayload.index === index) {
          if (actionPayload.changes.plz != null) {
            return {
              ...vermietetesObjekt,
              orte: [],
              strassen: [],
              lastError: null,
              ort: null,
              strasse: vermietetesObjekt.strasse === KEINE_AUSWAHL ? KEINE_AUSWAHL : null,
              ...actionPayload.changes,
            };
          }
          return {
            ...vermietetesObjekt,
            ...actionPayload.changes,
          };
        }
        return vermietetesObjekt;
      });
    }
    case RsActions.VERMIETETES_BEBAUTES_MODAL_OBJEKT_CHANGED: {
      const actionPayload: VermietetesBebautesModalObjektChangedPayload = action.payload;
      return sectionState.map((vermietetesBebautesObjekt, index) =>
        actionPayload.index === index
          ? applyBebautesObjektChanges(vermietetesBebautesObjekt, actionPayload.changes)
          : vermietetesBebautesObjekt,
      );
    }
    case RsActions.VERMIETETES_UNBEBAUTES_MODAL_OBJEKT_CHANGED: {
      const actionPayload: VermietetesUnbebautesModalObjektChangedPayload = action.payload;
      return sectionState.map((vermietetesUnbebautesObjekt, index) => {
        if (actionPayload.index === index) {
          if (actionPayload.changes.grundstueckBebaut === VermietetesObjektEnum.UNBEBAUT) {
            return {
              ...vermietetesUnbebautesObjekt,
              strasse: vermietetesUnbebautesObjekt.strasse || KEINE_AUSWAHL,
              grundstueckBebaut: VermietetesObjektEnum.UNBEBAUT,
              grundstueckFlaeche: null,
              flurstueckNummer: null,
            };
          }
          if (vermietetesUnbebautesObjekt.grundstueckBebaut === VermietetesObjektEnum.UNBEBAUT) {
            return {
              ...vermietetesUnbebautesObjekt,
              ...actionPayload.changes,
            };
          }
        }
        return vermietetesUnbebautesObjekt;
      });
    }
    case RsActions.VERMIETETES_MODAL_OBJEKT_ADDED: {
      return [...sectionState, getBasisVermietetesObjekt()];
    }
    case RsActions.VERMIETETES_MODAL_OBJEKT_DELETED: {
      const actionPayload: number = action.payload;
      return sectionState.filter((val, index) => index !== actionPayload);
    }
    case RsActions.VERMIETER_MODAL_IS_OPEN_CHANGED: {
      const actionPayload: boolean = action.payload;
      if (actionPayload === true) {
        const vermieteteObjekte = appState.vermieteteObjekte;
        if (vermieteteObjekte != null) {
          return vermieteteObjekte.map(vermietetesObjekt => {
            return {
              ...vermietetesObjekt,
              strassen: [],
              lastError: null,
              orte: [],
            } as VermietetesModalObjekt;
          });
        } else {
          return [getBasisVermietetesObjekt()];
        }
      } else {
        return [];
      }
    }
    case GlobalActions.ADDRESSVORSCHLAG_SUCCESSFUL: {
      const payload: AdressvorschlagPayload = action.payload;
      if (payload.callerId === CALLER_ID.RS_VERMIETETE_OBJEKTE) {
        return sectionState.map((vermietetesObjekt, index) => {
          if (payload.callerData.index === index) {
            let ort = payload.orte.length === 1 ? payload.orte[0] : null;
            if (vermietetesObjekt.vorbelegt) {
              if (payload.orte.includes(vermietetesObjekt.ort)) {
                ort = vermietetesObjekt.ort;
              }
            }
            return {
              ...vermietetesObjekt,
              ort: ort,
              lastError: null,
              orte: payload.orte,
              strassen: payload.strassen,
            };
          }
          return vermietetesObjekt;
        });
      }
      return sectionState;
    }
    case GlobalActions.ADDRESSVORSCHLAG_FAILED: {
      const payload: AdressvorschlagFailedPayload = action.payload;
      if (payload.callerId === CALLER_ID.RS_VERMIETETE_OBJEKTE) {
        return sectionState.map((vermietetesObjekt, index) => {
          if (payload.callerData.index === index) {
            const adressData: { orte: string[] | null; strassen: string[] | null } =
              payload.reason === ADRESSVORSCHLAG_FAILED_REASON.SERVICEDOWN
                ? { orte: null, strassen: null }
                : { orte: [], strassen: [] };
            return {
              ...vermietetesObjekt,
              lastError: payload.reason,
              ...adressData,
            };
          }
          return vermietetesObjekt;
        });
      }
      return sectionState;
    }
    default: {
      return sectionState;
    }
  }
}

export function getBasisVermietetesObjekt(): VermietetesBasisModalObjekt {
  return {
    strassen: [],
    lastError: null,
    orte: [],
    grundstueckBebaut: null,
    hausnummer: null,
    ort: null,
    plz: null,
    strasse: null,
  };
}
